/**
 * 仪表盘页面
 *
 * 展示全局统计数据 + 最近创建的品牌列表。
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import PeopleIcon from '@mui/icons-material/People'
import NoteAltIcon from '@mui/icons-material/NoteAlt'
import PaidIcon from '@mui/icons-material/Paid'
import { AdminLayout } from '@/components/AdminLayout'
import { StatCard } from '@/components/StatCard'
import { getDashboard } from '@/api/services'
import type { DashboardStats } from '@chaoyinli/shared'
import { formatDate, formatNumber } from '@/utils/format'

/** 品牌状态颜色映射 */
const STATUS_COLORS: Record<string, string> = {
  trial: '#FF9800',
  active: '#4CAF50',
  expired: '#F44336',
}

const STATUS_LABELS: Record<string, string> = {
  trial: '试用中',
  active: '已激活',
  expired: '已过期',
}

export function DashboardPage() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    getDashboard()
      .then((data) => {
        setStats(data)
        setError(null)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : '加载数据失败')
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <AdminLayout>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 3 }}>
        仪表盘
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#FF8C00' }} />
        </Box>
      ) : error ? (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 3, textAlign: 'center', color: '#F44336' }}>
            {error}
          </CardContent>
        </Card>
      ) : stats ? (
        <>
          {/* 统计卡片 */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <StatCard
                title="品牌总数"
                value={formatNumber(stats.total_brands)}
                icon={<BusinessIcon />}
                color="#FF8C00"
                subtitle={`试用 ${stats.trial_brands} · 激活 ${stats.active_brands} · 过期 ${stats.expired_brands}`}
              />
            </Grid>
            <Grid item xs={3}>
              <StatCard
                title="活跃品牌"
                value={formatNumber(stats.active_brands)}
                icon={<BusinessIcon />}
                color="#4CAF50"
              />
            </Grid>
            <Grid item xs={3}>
              <StatCard
                title="员工总数"
                value={formatNumber(stats.total_employees)}
                icon={<PeopleIcon />}
                color="#2196F3"
              />
            </Grid>
            <Grid item xs={3}>
              <StatCard
                title="笔记总数"
                value={formatNumber(stats.total_notes)}
                icon={<NoteAltIcon />}
                color="#9C27B0"
              />
            </Grid>
          </Grid>

          {/* 收入统计 */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#E8F5E9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4CAF50',
                  }}
                >
                  <PaidIcon />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '13px' }}>
                    累计收入
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                    ¥ {formatNumber((stats.total_revenue ?? 0) / 100)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 最近创建的品牌 */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1.5 }}>
            最近创建的品牌
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #f0f0f0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#666' }}>品牌名称</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#666' }}>子品牌</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#666' }}>品类</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#666' }}>状态</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#666' }}>创建时间</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.recent_brands && stats.recent_brands.length > 0 ? (
                  stats.recent_brands.map((brand) => (
                    <TableRow
                      key={brand.id}
                      hover
                      onClick={() => navigate(`/brands/${brand.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: '#333' }}>{brand.name}</TableCell>
                      <TableCell sx={{ color: '#888' }}>{brand.sub_brand || '-'}</TableCell>
                      <TableCell sx={{ color: '#888' }}>{brand.category || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[brand.status] || brand.status}
                          size="small"
                          sx={{
                            backgroundColor: `${STATUS_COLORS[brand.status]}15`,
                            color: STATUS_COLORS[brand.status],
                            fontSize: '12px',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#999' }}>{formatDate(brand.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: '#999', py: 3 }}>
                      暂无品牌数据
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </AdminLayout>
  )
}

export default DashboardPage
